import { Link, useParams } from "react-router-dom";
import { Button } from "../../../../components/ui/button";
import { SheetFooter, SheetTitle } from "../../../../components/ui/sheet";
import { useGetEphemeralUsersAdminHttp } from "../../../../hooks/http/admin/campaigns/get-ephemeral-user.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import type { IUser } from "../../../../types/models/user.models.types";
import { useEffect, useState } from "react";
import Spinner from "../../../../components/spinner.components";
import { Separator } from "../../../../components/ui/separator";
import { useNudgeEphemeralUserAdminHttp } from "../../../../hooks/http/admin/campaigns/nudge-ephemeral-user.admin.http";
import CheckIcon from "../../../../assets/svgs/common/check.svg";
import CrossIcon from "../../../../assets/svgs/common/cross.svg";

export default function BrandManagersSliderMenuPartial() {
  const { campaignId } = useParams();
  const [ephemeralUsers, setEphemeralUsers] = useState<
    (IUser & { visibleToBrandEnabled: boolean | null | undefined })[]
  >([]);
  const { toast } = useToast();
  const { execute: getEphemeralUsersHttp, fetching: fetchingEphemeralUsers } =
    useGetEphemeralUsersAdminHttp();
  const {
    execute: nudgeEphemeralUserHttp,
    fetching: nudgeEphemeralUserFetching,
  } = useNudgeEphemeralUserAdminHttp();

  async function nudgeEphemeralUser() {
    if (!campaignId) {
      return;
    }
    if (ephemeralUsers.length === 0) {
      toast({
        content: "Add brand managers to nudge",
        toastType: "error",
      });
      return;
    }
    const response = await nudgeEphemeralUserHttp({
      body: null,
      pathParams: { campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Nudge sent successfully",
      toastType: "success",
    });
  }

  async function getEphemeralUsers() {
    if (!campaignId) {
      return;
    }
    const response = await getEphemeralUsersHttp({
      body: null,
      pathParams: { campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setEphemeralUsers(response.data.users);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getEphemeralUsers();
  }, [campaignId]);

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex flex-col gap-8 w-full">
        <SheetTitle className="flex justify-between items-center pr-16">
          <p className="text-2xl text-white">Brand Managers</p>
          <Button
            disabled={nudgeEphemeralUserFetching || fetchingEphemeralUsers}
            onClick={nudgeEphemeralUser}
            className="bg-tertiarybuttonbg border border-border w-36 h-10 font-semibold"
          >
            {nudgeEphemeralUserFetching ? (
              <Spinner width={15} />
            ) : (
              "Nudge for Review"
            )}
          </Button>
        </SheetTitle>
        <div className="flex flex-col w-full">
          {fetchingEphemeralUsers ? (
            <div className="flex justify-center items-center h-40">
              <Spinner width={20} />
            </div>
          ) : (
            <div className="">
              <div className="grid grid-cols-3 items-center px-4 gap-4">
                <p className="py-4 text-secondary text-xs">Brand managers</p>
                <p className="py-4 text-secondary text-xs text-right">Phone</p>
                <p className="py-4 text-secondary text-xs text-right">
                  Show only shortlisted drafts
                </p>
              </div>
              <Separator />
              {ephemeralUsers.map((user, index) => (
                <div key={user.id} className="flex flex-col w-full">
                  <div className="grid grid-cols-3 items-center px-4 h-14 gap-4">
                    <div className="font-semibold text-sm text-white">
                      {user.name}
                    </div>
                    <div className="text-secondary text-right">
                      {user.identifier ? user.identifier.slice(0, 13) : "-"}
                    </div>
                    <div className="flex justify-end items-center">
                      {user.visibleToBrandEnabled ? (
                        <CheckIcon />
                      ) : (
                        <CrossIcon />
                      )}
                    </div>
                  </div>

                  {index !== ephemeralUsers.length - 1 && (
                    <Separator className="w-full" />
                  )}
                </div>
              ))}
            </div>
          )}
          {!fetchingEphemeralUsers && ephemeralUsers.length === 0 && (
            <div className="flex justify-center items-center h-40 text-sm">
              No brand managers found
            </div>
          )}
        </div>
      </div>

      <SheetFooter>
        <Link
          className="w-full"
          to={`/admin/campaigns/${campaignId}/campaign-metrics/add-brand-manager`}
        >
          <Button className="bg-primarybuttonbg w-full h-14 font-semibold text-black">
            Add Brand Manager
          </Button>
        </Link>
      </SheetFooter>
    </div>
  );
}
